/**
 * MonetizationStep
 *
 * Fifth step of the add content flow. Lets the creator enable/disable
 * premium-video payment, choose a Boost level, set an unlock price and review
 * a monetization/costs overview before publishing.
 * Mirrors Flutter's ContentMonetizationScreen from
 * lib/presentation/screens/publish/content_monetization_screen.dart.
 */

import React, { useCallback } from 'react';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useRegisterStepNav } from '@/presentation/components/common/stepper';
import { Divider } from '@/presentation/components/common/Divider';
import { useContentMonetization } from '../../hooks/useContentMonetization';
import { usePublishContent } from '../../hooks/usePublishContent';
import { PremiumContentToggle } from './monetizationStep/PremiumContentToggle';
import { BoostSection } from './monetizationStep/BoostSection';
import { AllowUnlockSection } from './monetizationStep/AllowUnlockSection';
import { MonetizationOverview } from './monetizationStep/MonetizationOverview';
import type { ContentReplyCommentInfoEntity } from '@/domain/entities/comment/contentReplyCommentInfoEntity';
import type { VideoMeta } from '../../hooks/useAddContent';
import type { DescriptionResult } from '../../hooks/useContentDescription';
import type { ContentSettingsResult } from '../../hooks/useContentSettings';

interface MonetizationStepProps {
  mediaId: string;
  selectedFile: File | null;
  videoMeta: VideoMeta | null;
  descriptionResult: DescriptionResult | null;
  contentSettings: ContentSettingsResult | null;
  onBack: () => void;
  onFinish: () => void;
  screenKey?: string;
  /**
   * Comment-reply flow only — forwarded verbatim to `usePublishContent` so it
   * rides the `transcodeUserVideo` payload. The monetization UI itself is
   * identical for the comment-reply flow: Flutter runs the very same
   * `ContentMonetizationScreen`, which only passes
   * `widget.contentReplyCommentInfo` through to `uploadMedia`
   * (lib/presentation/screens/publish/content_monetization_screen.dart:241-253).
   */
  contentReplyCommentInfo?: ContentReplyCommentInfoEntity | null;
  /**
   * Comment-reply flow only — the web equivalent of Flutter's `bool uploaded`
   * pop value. See the contract on `UsePublishContentParams.onUploaded`.
   */
  onUploaded?: (success: boolean) => void;
}

export const MonetizationStep: React.FC<MonetizationStepProps> = ({
  mediaId,
  selectedFile,
  videoMeta,
  descriptionResult,
  contentSettings,
  onBack,
  onFinish,
  screenKey,
  contentReplyCommentInfo,
  onUploaded,
}) => {
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const durationSeconds = videoMeta?.duration ?? 0;

  const {
    allowPremiumContentPayment,
    isBoosted,
    boosts,
    allowUnlock,
    unlockChargeUSD,
    unlockPriceCoins,
    unlockPriceInputText,
    isFreeContent,
    premiumVideoPriceCoins,
    boostPriceCoins,
    shouldRecharge,
    rechargeAmount,
    canSave,
    togglePremium,
    toggleBoosted,
    selectBoost,
    cycleBoostOption,
    toggleAllowUnlock,
    onUnlockPriceChange,
    setUnlockInputFocus,
    handleRechargeWallet,
    buildResult,
  } = useContentMonetization({ durationSeconds, screenKey });

  const isDraft = contentSettings?.isDraft ?? false;
  const isScheduled = contentSettings?.schedule != null;

  const publishLabel = isDraft
    ? l10n.publish.saveDraftButton
    : isScheduled
      ? l10n.publish.publishScheduleButton
      : l10n.publish.publishButton;

  const { publish } = usePublishContent({
    mediaId,
    selectedFile,
    videoMeta,
    descriptionResult,
    contentSettings,
    onFinish,
    screenKey,
    contentReplyCommentInfo,
    onUploaded,
  });

  const handlePublish = useCallback(async () => {
    if (!canSave) return;
    await publish(buildResult());
  }, [canSave, buildResult, publish]);

  useRegisterStepNav(
    {
      onNext: handlePublish,
      canGoNext: canSave,
      nextLabel: publishLabel,
      previousLabel: l10n.publish.previousButton,
      onPrevious: onBack,
    },
    [handlePublish, canSave, publishLabel, l10n.publish.previousButton, onBack],
  );

  return (
    <div
      className="scrollbar-hide"
      style={{
        height: '100%',
        overflowY: 'auto',
        padding: `0 ${responsive.spacing.xl}px`,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        <div style={{ paddingTop: responsive.spacing.xl, display: 'flex', flexDirection: 'column' }}>
          {!isFreeContent && (
            <>
              <PremiumContentToggle
                enabled={allowPremiumContentPayment}
                premiumPriceCoins={premiumVideoPriceCoins}
                onChange={togglePremium}
              />
              <Divider />
            </>
          )}

          <BoostSection
            enabled={isBoosted}
            boosts={boosts}
            boostPriceCoins={boostPriceCoins}
            isDraft={isDraft}
            isScheduled={isScheduled}
            onChange={toggleBoosted}
            onSelectBoost={selectBoost}
            onCycleOption={cycleBoostOption}
          />

          <Divider />

          <AllowUnlockSection
            enabled={allowUnlock}
            inputText={unlockPriceInputText}
            unlockChargeUSD={unlockChargeUSD}
            onToggle={toggleAllowUnlock}
            onChangeText={onUnlockPriceChange}
            onFocus={() => setUnlockInputFocus(true)}
            onBlur={() => setUnlockInputFocus(false)}
          />

          <Divider />

          <MonetizationOverview
            allowPremium={allowPremiumContentPayment}
            premiumPriceCoins={premiumVideoPriceCoins}
            isBoosted={isBoosted}
            boostPriceCoins={boostPriceCoins}
            allowUnlock={allowUnlock}
            unlockPriceCoins={unlockPriceCoins}
            shouldRecharge={shouldRecharge}
            rechargeAmount={rechargeAmount}
            onRechargeClick={handleRechargeWallet}
          />
        </div>

      </div>
    </div>
  );
};
